import { useState } from 'react';
import { Navigate, useNavigate, useParams } from '@tanstack/react-router';
import type { SubmitTaskRequestTypeEnum } from '@bvengineering/cas-member-gateway-service-client';
import type { CreatorTaskType } from '@bvengineering/cas-analytics-events';
import { Button, Icon, IconButton, Typography, useToast } from '@/primer/components';
import { PageColumn, PageLoadingState } from '@/components/layout';
import { PostVerificationFeedback } from '@/components/ui';
import { useCampaign, useSubmitTask } from '@/api';
import { useIsProviderConnected } from '@/api/queries/settings/useQuerySocialConnections';
import { useMixpanel } from '@/contexts';
import { trackFunnelEvent, CREATOR_FUNNEL_EVENTS } from '@/utils';
import { getTaskDisplay, isSupportedTaskType } from '@/utils/taskTypes';
import {
  isTaskCompleted,
  isTaskDisabled,
  isTaskSubmittable,
  isTaskValidating,
  isTaskRejected,
} from '@/utils/taskState';
import { getTaskRejectionModalCopy } from '@/utils/taskRejectionReasons';

interface NumberedStepProps {
  index: number;
  title: string;
  children?: React.ReactNode;
}

/** Read-only pill displaying a value (account handle or mention) to include in the post. */
function ValuePill({ value }: { value: string }) {
  return (
    <div className="w-full rounded-medium bg-interactive-quiet px-4 py-3">
      <Typography variant="body" size="medium" as="span" decoration={{ bold: true }}>
        {value}
      </Typography>
    </div>
  );
}

/** A single numbered step in the "Follow these steps" list. */
function NumberedStep({ index, title, children }: NumberedStepProps) {
  return (
    <li className="flex gap-3">
      <Typography variant="body" size="medium" as="span" decoration={{ bold: true }}>
        {index}.
      </Typography>
      <div className="flex flex-col gap-2 flex-1 min-w-0">
        <Typography variant="body" size="medium" as="span">
          {title}
        </Typography>
        {children}
      </div>
    </li>
  );
}

/** Shown while the submitted post is being verified by the backend. */
function VerifyingState() {
  return (
    <div className="flex flex-col items-center gap-3 py-12 text-center">
      <Icon name="clock" size="xl" color="interactive" />
      <Typography variant="header" size="h2" as="h2">
        Verifying your post
      </Typography>
      <Typography variant="body" size="small" color="bodylight">
        Usually takes a few minutes. We will update your task once verification is complete.
      </Typography>
    </div>
  );
}

/** Shown once the submitted post has been verified. */
function CompletedState({ onBack }: { onBack: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 py-12 text-center">
      <Icon name="circle-tick-filled" size="xl" color="success" />
      <Typography variant="header" size="h2" as="h2">
        Your post is verified
      </Typography>
      <Typography variant="body" size="small" color="bodylight">
        This task is complete.
      </Typography>
      <Button variant="primary" size="large" fullWidth onClick={onBack}>
        Back to tasks
      </Button>
    </div>
  );
}

/**
 * Post on Instagram — social task submission screen. Renders the post steps
 * (performable/rejected), an explicit "I've posted" submit, and the in-place
 * verifying state; rejected tasks can be re-submitted via "Verify again".
 */
export function PostOnInstagram() {
  // strict:false because this route is un-nested from the tasks hub (trailing-
  // underscore id). Missing/invalid params fail fast to the Task Not Found state below.
  const { id, taskId } = useParams({ strict: false });
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { mixpanel } = useMixpanel();

  const campaignId = Number(id);
  const numericTaskId = Number(taskId);

  const { data: campaign, isLoading, error } = useCampaign(campaignId);
  const { isConnected, username: instagramUsername } = useIsProviderConnected('INSTAGRAM');
  const submitTask = useSubmitTask();
  const [verificationAttempt, setVerificationAttempt] = useState(0);

  const task = campaign?.tasks?.find((candidate) => candidate.id === numericTaskId);

  const backToTasks = (replace = false) => {
    // `id` comes from useParams({ strict: false }); guard against a missing/invalid
    // param so we never navigate to `/campaign/NaN/tasks`.
    if (Number.isFinite(campaignId)) {
      navigate({ to: '/campaign/$id/tasks', params: { id: campaignId }, replace });
    } else {
      navigate({ to: '/dashboard' });
    }
  };

  // On success the creator continues on the tasks hub, where the task shows as being verified.
  const handleSubmit = async () => {
    // Guard on the same conditions as `canSubmit` (below) so the mutation can't fire
    // for unsupported/non-submittable tasks or a disconnected account, even if invoked
    // outside the disabled-button path.
    if (
      !task ||
      submitTask.isPending ||
      !isConnected ||
      !isSupportedTaskType(task.type) ||
      !isTaskSubmittable(task.state)
    ) {
      return;
    }
    try {
      await submitTask.mutateAsync({
        taskId: task.id,
        campaignId,
        type: task.type as SubmitTaskRequestTypeEnum,
      });
      // A fast rejection can arrive before we ever render VALIDATING.
      setVerificationAttempt((attempt) => attempt + 1);
    } catch {
      showToast({ variant: 'error', message: 'Failed to submit. Please try again.' });
      return;
    }

    trackFunnelEvent(
      mixpanel,
      CREATOR_FUNNEL_EVENTS.TASK_SUBMITTED,
      {
        campaign_id: String(campaignId),
        campaign_name: campaign?.name ?? '',
        task_type: task.type.toUpperCase() as CreatorTaskType,
      },
      [campaign?.name]
    );
    backToTasks(true);
  };

  if (isLoading) {
    return <PageLoadingState />;
  }

  if (error || !campaign || !task) {
    return (
      <PageColumn className="text-center py-8">
        <div className="flex flex-col items-center gap-4">
          <Typography variant="header" size="h1" color="error" as="h1">
            Task Not Found
          </Typography>
          <Button onClick={() => backToTasks()}>Back to Tasks</Button>
        </div>
      </PageColumn>
    );
  }

  if (isTaskDisabled(task.state)) {
    return <Navigate to="/campaign/$id/tasks" params={{ id: campaignId }} replace />;
  }

  const display = getTaskDisplay(task.type);
  const handle = instagramUsername
    ? instagramUsername.startsWith('@')
      ? instagramUsername
      : `@${instagramUsername}`
    : null;
  const keywords = task.requiredKeywords ?? [];
  // Creator can only submit a performable/rejected task, and only with a connected account.
  const canSubmit = isConnected && isSupportedTaskType(task.type) && isTaskSubmittable(task.state);
  const validating = isTaskValidating(task.state);
  const completed = isTaskCompleted(task.state);
  const rejected = isTaskRejected(task.state);

  return (
    <PageColumn className="py-4 flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <IconButton icon="chevron-left" onClick={() => backToTasks()} aria-label="Go back" />
        <Typography variant="header" size="h1" as="h1">
          {campaign.name ?? 'Your program'}
        </Typography>
      </div>

      {validating ? (
        <VerifyingState />
      ) : completed ? (
        <CompletedState onBack={() => backToTasks()} />
      ) : (
        <>
          <Typography variant="header" size="h2" as="h2">
            Follow these steps below on {display.platform}
          </Typography>

          <ol className="flex flex-col gap-5">
            <NumberedStep index={1} title="Log in to the account below to post">
              {handle ? (
                <ValuePill value={handle} />
              ) : (
                <Typography variant="body" size="small" color="bodylight">
                  Make sure you post from the Instagram account you connected to Influenster.
                </Typography>
              )}
            </NumberedStep>

            <NumberedStep index={2} title="Add the content you've captured" />

            {keywords.length > 0 && (
              <NumberedStep index={3} title="Mention these handles:">
                <div className="flex flex-col gap-2">
                  {keywords.map((keyword) => (
                    <ValuePill key={keyword} value={keyword} />
                  ))}
                </div>
              </NumberedStep>
            )}
          </ol>

          <div className="flex flex-col gap-1 rounded-large bg-surface p-5 text-center shadow-(--shadow-top-light-first-floor)">
            <Typography variant="header" size="h3" as="h3">
              Final check
            </Typography>
            <Typography variant="body" size="small" color="bodylight">
              Please ensure every step above is done to complete this task.
            </Typography>
          </div>

          {rejected ? (
            // Mounted only for REJECTED: a new task/reason or verification cycle resets dismissal.
            <PostVerificationFeedback
              key={`${campaignId}:${task.id}:${task.type}:${task.stateReason ?? ''}:${verificationAttempt}`}
              reason={getTaskRejectionModalCopy(task.stateReason, task.type)}
              canSubmit={canSubmit}
              isPending={submitTask.isPending}
              onVerify={handleSubmit}
            />
          ) : (
            <div className="flex flex-col gap-3">
              <div className="text-center">
                <Typography variant="body" size="small" color="bodylight">
                  {isConnected
                    ? 'Have you published your post?'
                    : 'Connect your Instagram account in settings to submit this task.'}
                </Typography>
              </div>
              <Button
                variant="primary"
                size="large"
                fullWidth
                onClick={handleSubmit}
                disabled={!canSubmit || submitTask.isPending}
                isLoading={submitTask.isPending}
              >
                I've posted!
              </Button>
            </div>
          )}
        </>
      )}
    </PageColumn>
  );
}

export default PostOnInstagram;
